import { useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import EmptyState from 'components/EmptyState/EmptyState';
import FilterSearchCard from 'components/Filter/FilterSearchCard';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import useBadges from '../../../api/useBadges';
import { Badge, EmblemType, RibbonType } from '../../../common/contracts';
import { capitalizeFirstLetter } from '../../../utils/formattingUtil';
import { permissionsUtil } from '../../../utils/permissionsUtil';
import { EToasterStatus, showToast } from '../../../utils/showToast';

const dayjsUTC = dayjs.extend(utc);

const BadgesTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { getBadges, deleteBadge } = useBadges(publisherId);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBadgeId, setSelectedBadgeId] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState('');

  const handleConfirmDelete = () => {
    if (!selectedBadgeId) return;
    deleteBadge.mutate(selectedBadgeId, {
      onSuccess: () => {
        showToast({
          message: 'Badge deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getBadges.refetch();
        setShowDeleteModal(false);
        setSelectedBadgeId(null);
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete badge',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
        setShowDeleteModal(false);
        setSelectedBadgeId(null);
      }
    });
  };

  const getBadgeImage = (emblem: EmblemType, ribbon: RibbonType) => {
    const colorOne = ribbon?.backgroundColor?.colorOne;
    const colorTwo = ribbon?.backgroundColor?.colorTwo;
    const gradientDirection =
      ribbon?.backgroundColor?.gradientDirection || 'to bottom';
    const imageUrl = emblem?.imageUrl || ribbon?.imageUrl;

    if (imageUrl) {
      return (
        <div
          className="w-10 h-10 rounded bg-cover bg-center"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
      );
    }

    if (colorOne && colorTwo) {
      return (
        <div
          className="w-10 h-10 rounded"
          style={{
            backgroundImage: `linear-gradient(${gradientDirection}, ${colorOne}, ${colorTwo})`
          }}
        />
      );
    }

    return (
      <div
        className="w-10 h-10 rounded"
        style={{ backgroundColor: colorOne }}
      />
    );
  };

  const COLUMNS: ColumnDef<any>[] = [
    {
      accessorKey: 'emblemOrRibbon',
      header: 'ID',
      cell: ({ row }: any) => {
        const { emblem, ribbon } = row.original;
        return getBadgeImage(emblem, ribbon);
      }
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => getValue(),
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.name || '';
        const nameB = rowB.original.name || '';
        return nameA.localeCompare(nameB);
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created at',
      cell: ({ getValue }: any) =>
        dayjsUTC.utc(getValue()).format('MMM DD, YYYY'),
      sortDescFirst: true,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.createdAt;
        const dateB = rowB.original.createdAt;

        // Handle null/undefined values - put them at the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dayjsUTC.utc(dateB).diff(dayjsUTC.utc(dateA));
      }
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }: any) => capitalizeFirstLetter(getValue())
    },
    {
      accessorKey: 'publisherBadgeId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: any) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => navigate(`./form/${row.original.badgeId}`)
            },
            {
              label: 'Duplicate',
              handler: () => navigate(`./dup/${row.original.badgeId}`),
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.BADGES,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            },
            {
              label: 'Delete',
              handler: () => {
                if (!row.original.badgeId) return;
                setSelectedBadgeId(row.original.badgeId);
                setShowDeleteModal(true);
              },
              disabled:
                !permissionsUtil.canUserEdit() ||
                !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.BADGES,
                  EPermissionAction.MANAGE,
                  publisherId
                )
            }
          ]}
        />
      )
    }
  ];

  const badges: Badge[] = useMemo(() => {
    if (getBadges.isLoading || !getBadges.data) {
      return [];
    }

    return getBadges.data.result;
  }, [getBadges.data, getBadges.isLoading]);

  const filteredBadges = useMemo(() => {
    return [...badges].reverse().filter((badge) => {
      const lowerName = badge.name.toLowerCase();
      return lowerName.includes(nameFilter.toLowerCase());
    });
  }, [badges, nameFilter]);

  const BUTTON_TEXT = `${badges.length > 0 ? 'Add' : 'Create'} New Badge`;

  const renderContent = () => {
    if (getBadges.isLoading || getBadges.isFetching) {
      return (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      );
    }

    if (badges.length === 0) {
      return (
        <EmptyState
          title="Add your first Badge"
          description="Currently there is no badges in your store"
          buttonText={BUTTON_TEXT}
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredBadges}
          columns={COLUMNS}
          rowIdIdentifier="offerId"
          bodyDataTestId="dashPopupsList"
          rowsPerPage={badges.length}
          showPagination={false}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title="Badges"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !permissionsUtil.canUserEdit(),
          hidden: !permissionsUtil.isActionEnabled(
            null,
            EPermissionGroup.BADGES,
            EPermissionAction.MANAGE,
            publisherId
          )
        }}
        isCreateButton
      />
      {badges.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Badge Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
            isLabelSearchBy
          />
        </div>
      )}
      {renderContent()}
      {showDeleteModal && (
        <DialogModal
          isOpen={showDeleteModal}
          onOpenChange={(open) => {
            if (!open) setShowDeleteModal(false);
          }}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_BADGE].title}
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_BADGE].description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleConfirmDelete },
            onClose: {
              label: 'Cancel',
              action: () => setShowDeleteModal(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default BadgesTable;
